/**
 * ProjectDetailPage.jsx — Proje Detay Sayfası
 * Route: /Management/Projects/:id
 * Roller: [1,2,3]
 *
 * KURAL: chooseProjectFile / updateProject sonrası getProjectDetail ile reload et — local state manipülasyonu yapma.
 * KURAL: uploadProjectFile FormData kullanır (projectService.js bunu halleder).
 * KURAL: 500 hataları plain-text gelir — err.message kullan.
 */

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader,
  Row, Col, Button, Badge, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Input, FormGroup, Label,
} from "reactstrap";
import {
  ArrowLeft, RefreshCw, Upload, Trash2, CheckCircle, XCircle,
  FileText, MessageSquare, Plus, Leaf, Edit2,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getProjectDetail,
  uploadProjectFile,
  deleteProjectFile,
  chooseProjectFile,
  setFileApproval,
  updateProject,
  addRevision,
  deleteRevision,
  deleteProject,
  setTextContent,
  FILE_APPROVAL_STATUS,
  PROJECT_STATUS,
  DEAL_STATUS,
  FINANCE_APPROVAL,
} from "../../../services/projectService";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../i18n";

// ─── Yardımcılar ──────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusBadge({ value, map }) {
  const s = map?.[value];
  if (!s) return <Badge color="secondary">—</Badge>;
  return <Badge color={s.color}>{s.label}</Badge>;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ProjectDetailPage() {
  const { t } = useTranslation("projeler");
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  // Aktif sekme
  const [activeTab, setActiveTab] = useState("files");

  // Dosya yükleme modal
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadStandFileId, setUploadStandFileId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef();

  // Revizyon modal
  const [revisionModal, setRevisionModal] = useState(false);
  const [revisionText, setRevisionText] = useState("");

  // Silme onay modal
  const [deleteModal, setDeleteModal] = useState(false);

  // Durum güncelleme modal
  const [statusModal, setStatusModal] = useState(false);
  const [editStatusId, setEditStatusId] = useState("");
  const [editDealStatus, setEditDealStatus] = useState("");
  const [editFinanceApproval, setEditFinanceApproval] = useState("");
  const [editFinanceText, setEditFinanceText] = useState("");

  // ─── Veri yükle ───────────────────────────────────────────────────────────
  async function loadDetail() {
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectDetail(id);
      setProject(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => { await loadDetail(); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ─── Geçici başarı mesajı ─────────────────────────────────────────────────
  function showSuccess(msg) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }

  // ─── Dosya işlemleri ──────────────────────────────────────────────────────
  async function handleUpload() {
    if (!uploadStandFileId || !uploadFile) return;
    setActionLoading(true);
    try {
      await uploadProjectFile(id, Number(uploadStandFileId), uploadFile);
      setUploadModal(false);
      setUploadStandFileId("");
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadDetail(); // ← Kural 2: reload
      showSuccess(t("projectDetail.dosyaYuklendi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleChooseFile(fileId) {
    setActionLoading(true);
    try {
      await chooseProjectFile(id, fileId);
      await loadDetail(); // ← Kural 2: choose sonrası reload
      showSuccess(t("projectDetail.dosyaSecildiVeOnaylandi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteFile(fileId) {
    setActionLoading(true);
    try {
      await deleteProjectFile(id, fileId, true);
      await loadDetail();
      showSuccess(t("projectDetail.dosyaSilindi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSetFileApproval(standFileId, status) {
    setActionLoading(true);
    try {
      await setFileApproval(id, standFileId, status);
      await loadDetail();
      showSuccess(t("projectDetail.onayDurumuGuncellendi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Revizyon ─────────────────────────────────────────────────────────────
  async function handleAddRevision() {
    if (!revisionText.trim()) return;
    setActionLoading(true);
    try {
      await addRevision(id, revisionText.trim());
      setRevisionModal(false);
      setRevisionText("");
      await loadDetail();
      showSuccess(t("projectDetail.revizyonEklendi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteRevision(revisionId) {
    setActionLoading(true);
    try {
      await deleteRevision(id, revisionId);
      await loadDetail();
      showSuccess(t("projectDetail.revizyonSilindi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Durum güncelle ───────────────────────────────────────────────────────
  function openStatusModal() {
    setEditStatusId(project.statusId ?? "");
    setEditDealStatus(project.dealStatus ?? "");
    setEditFinanceApproval(project.financeApproval ?? "");
    setEditFinanceText(project.financeText ?? "");
    setStatusModal(true);
  }

  async function handleUpdateStatus() {
    setActionLoading(true);
    try {
      const body = {};
      if (editStatusId !== "") body.statusId = Number(editStatusId);
      if (editDealStatus !== "") body.dealStatus = Number(editDealStatus);
      if (editFinanceApproval !== "") body.financeApproval = Number(editFinanceApproval);
      if (editFinanceText !== "") body.financeText = editFinanceText;

      await updateProject(id, body);
      setStatusModal(false);
      await loadDetail(); // ← Kural 2: reload
      showSuccess(t("projectDetail.projeGuncellendi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Sil ──────────────────────────────────────────────────────────────────
  async function handleDelete() {
    setActionLoading(true);
    try {
      await deleteProject(id);
      navigate("/Management/Projects");
    } catch (e) {
      setError(e.message);
      setDeleteModal(false);
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="page-content">
      <Container fluid className="text-center py-5">
        <Spinner color="primary" />
      </Container>
    </div>
  );

  if (error && !project) return (
    <div className="page-content">
      <Container fluid>
        <Alert color="danger">{error}</Alert>
        <Button color="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} className="me-1" />{" "}{t("common:geri")}
        </Button>
      </Container>
    </div>
  );

  if (!project) return null;

  // Onay durumu rengi için file approval tablosu
  const fileApprovalsMap = {};
  (project.fileApprovals || []).forEach(fa => {
    fileApprovalsMap[fa.standFileId] = fa;
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("projectDetail.proje", { v: project.standName || id })}
          pageTitle={t("projectDetail.projeler")}
        />

        {successMsg && <Alert color="success">{successMsg}</Alert>}
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        {/* ─── Başlık Bar ─── */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Button color="secondary" outline size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} className="me-1" />{" "}{t("common:geri")}
          </Button>
          <div className="d-flex gap-2">
            <Button color="info" outline size="sm" onClick={loadDetail}>
              <RefreshCw size={14} />
            </Button>
            <Button color="warning" outline size="sm" onClick={openStatusModal}>
              <Edit2 size={14} className="me-1" />{" "}{t("projectDetail.durumGuncelle")}
            </Button>
            <Button
              color="success"
              outline
              size="sm"
              onClick={() => navigate(`/Management/Projects/${id}/sustainability`)}
            >
              <Leaf size={14} className="me-1" />{" "}{t("projectDetail.surdurulebilirlik")}
            </Button>
            <Button color="danger" outline size="sm" onClick={() => setDeleteModal(true)}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        {/* ─── Özet Kart ─── */}
        <Card className="mb-3">
          <CardBody>
            <Row>
              <Col md={3}>
                <div className="text-muted small">{t("projectDetail.stand")}</div>
                <div className="fw-semibold">{project.standName}</div>
                <div className="text-muted small">{project.salonName}</div>
              </Col>
              <Col md={3}>
                <div className="text-muted small">{t("projectDetail.firma")}</div>
                <div>{project.firmaName || "—"}</div>
                <div className="text-muted small">{t("projectDetail.dekorator")}{" "}{project.dekoratorFirmaName || "—"}</div>
              </Col>
              <Col md={2}>
                <div className="text-muted small">{t("projectDetail.tipYukseklik")}</div>
                <div>{project.standTypeName || project.standTypeId}</div>
                <div className="text-muted small">{project.standHeightName} — {project.projectHeightNumber}m</div>
              </Col>
              <Col md={2}>
                <div className="text-muted small">{t("projectDetail.durum")}</div>
                <StatusBadge value={project.statusId} map={PROJECT_STATUS} />
                <div className="mt-1">
                  <StatusBadge value={project.dealStatus} map={DEAL_STATUS} />
                </div>
              </Col>
              <Col md={2}>
                <div className="text-muted small">{t("projectDetail.belgeOnayi")}</div>
                <div className="fw-semibold">
                  {project.approvedCount ?? (project.fileApprovals?.filter(f => f.status === 1).length ?? 0)}/
                  {project.requiredCount ?? (project.requiredFiles?.length ?? 0)}
                </div>
                <div className="text-muted small">{t("projectDetail.onayliBelge")}</div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ─── Sekmeler ─── */}
        <div className="d-flex gap-2 mb-3">
          {["files", "revisions", "textcontent"].map(tab => (
            <Button
              key={tab}
              color={activeTab === tab ? "primary" : "secondary"}
              outline={activeTab !== tab}
              size="sm"
              onClick={() => setActiveTab(tab)}
            >
              {tab === "files" && <><FileText size={13} className="me-1" />{t("projectDetail.belgeler")}</>}
              {tab === "revisions" && <><MessageSquare size={13} className="me-1" />{t("projectDetail.revizyonlar")}</>}
              {tab === "textcontent" && <><Edit2 size={13} className="me-1" />{t("projectDetail.metinAlanlari")}</>}
            </Button>
          ))}
        </div>

        {/* ─── BELGELER SEKMESİ ─── */}
        {activeTab === "files" && (
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{t("projectDetail.zorunluBelgelerVeYuklemeler")}</h6>
              <Button
                color="primary"
                size="sm"
                onClick={() => setUploadModal(true)}
                disabled={actionLoading}
              >
                <Upload size={13} className="me-1" />{" "}{t("projectDetail.dosyaYukle")}
              </Button>
            </CardHeader>
            <CardBody>
              {/* Zorunlu belgeler listesi */}
              {(project.requiredFiles || []).length === 0 ? (
                <Alert color="secondary">{t("projectDetail.zorunluBelgeTanimlanmamis")}</Alert>
              ) : (
                <div className="table-responsive">
                  <Table size="sm" hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>{t("projectDetail.belge")}</th>
                        <th>{t("projectDetail.onayDurumu")}</th>
                        <th>{t("projectDetail.yuklenenDosyalar")}</th>
                        <th>{t("projectDetail.islem")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(project.requiredFiles || []).map(rf => {
                        const fileId = rf.stand_file_id ?? rf.standFileId;
                        const fa = fileApprovalsMap[fileId];
                        const uploadedForThis = (project.uploadedFiles || []).filter(
                          u => u.standFileId === fileId
                        );
                        const approvalStatus = fa?.status ?? 0;
                        const approvalInfo = FILE_APPROVAL_STATUS[approvalStatus];

                        return (
                          <tr key={fileId}>
                            <td>
                              <strong>{rf.stand_file_name ?? rf.standFileName}</strong>
                              {rf.stand_file_shortname && (
                                <Badge color="light" className="ms-1 text-muted small">
                                  {rf.stand_file_shortname}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <Badge color={approvalInfo?.color ?? "secondary"}>
                                {approvalInfo?.label ?? "—"}
                              </Badge>
                            </td>
                            <td>
                              {uploadedForThis.length === 0 ? (
                                <span className="text-muted small">{t("projectDetail.dosyaYok")}</span>
                              ) : (
                                <div className="d-flex flex-column gap-1">
                                  {uploadedForThis.map(uf => (
                                    <div key={uf._id} className="d-flex align-items-center gap-2">
                                      <span className="small">{uf.originalFileName || uf.storedFileName}</span>
                                      {uf.isChosen && <Badge color="primary" className="small">{t("projectDetail.secili")}</Badge>}
                                      <div className="d-flex gap-1 ms-auto">
                                        {!uf.isChosen && (
                                          <Button
                                            color="success"
                                            size="sm"
                                            outline
                                            onClick={() => handleChooseFile(uf._id)}
                                            disabled={actionLoading}
                                            title={t("projectDetail.secVeOnayla")}
                                          >
                                            <CheckCircle size={12} />
                                          </Button>
                                        )}
                                        <Button
                                          color="danger"
                                          size="sm"
                                          outline
                                          onClick={() => handleDeleteFile(uf._id)}
                                          disabled={actionLoading}
                                          title={t("common:sil")}
                                        >
                                          <Trash2 size={12} />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  color="success"
                                  size="sm"
                                  outline
                                  onClick={() => handleSetFileApproval(fileId, 1)}
                                  disabled={actionLoading || approvalStatus === 1}
                                  title={t("projectDetail.onayla")}
                                >
                                  <CheckCircle size={12} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  onClick={() => handleSetFileApproval(fileId, 2)}
                                  disabled={actionLoading || approvalStatus === 2}
                                  title={t("projectDetail.reddet")}
                                >
                                  <XCircle size={12} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* ─── REVİZYONLAR SEKMESİ ─── */}
        {activeTab === "revisions" && (
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{t("projectDetail.revizyonNotlari")}</h6>
              <Button
                color="primary"
                size="sm"
                onClick={() => setRevisionModal(true)}
                disabled={actionLoading}
              >
                <Plus size={13} className="me-1" />{" "}{t("projectDetail.notEkle")}
              </Button>
            </CardHeader>
            <CardBody>
              {(project.revisions || []).length === 0 ? (
                <Alert color="secondary">{t("projectDetail.revizyonNotuYok")}</Alert>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {project.revisions.map((r, i) => (
                    <div key={r._id ?? i} className="border rounded p-2 d-flex justify-content-between align-items-start">
                      <div>
                        <div className="small">{r.text}</div>
                        <div className="text-muted small mt-1">{fmtDate(r.createdAt)} — {r.createdBy || "—"}</div>
                      </div>
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => handleDeleteRevision(r._id)}
                        disabled={actionLoading}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* ─── METİN ALANLARI SEKMESİ ─── */}
        {activeTab === "textcontent" && (
          <TextContentTab projectId={id} textContents={project.textContents || []} onReload={loadDetail} />
        )}

        {/* ─── MODAL: Dosya Yükleme ─── */}
        <Modal isOpen={uploadModal} toggle={() => setUploadModal(false)}>
          <ModalHeader toggle={() => setUploadModal(false)}>{t("projectDetail.dosyaYukle")}</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("projectDetail.belgeTipiStandFileId")}</Label>
              <Input
                type="select"
                value={uploadStandFileId}
                onChange={e => setUploadStandFileId(e.target.value)}
              >
                <option value="">{t("projectDetail.secin")}</option>
                {(project.requiredFiles || []).map(rf => {
                  const fid = rf.stand_file_id ?? rf.standFileId;
                  return (
                    <option key={fid} value={fid}>
                      {rf.stand_file_name ?? rf.standFileName}
                    </option>
                  );
                })}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>{t("projectDetail.dosya")}</Label>
              <Input
                type="file"
                innerRef={fileInputRef}
                onChange={e => setUploadFile(e.target.files[0] || null)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setUploadModal(false)}>{t("common:iptal")}</Button>
            <Button
              color="primary"
              onClick={handleUpload}
              disabled={actionLoading || !uploadStandFileId || !uploadFile}
            >
              {actionLoading ? <Spinner size="sm" /> : t("common:yukle")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── MODAL: Revizyon ─── */}
        <Modal isOpen={revisionModal} toggle={() => setRevisionModal(false)}>
          <ModalHeader toggle={() => setRevisionModal(false)}>{t("projectDetail.revizyonNotuEkle")}</ModalHeader>
          <ModalBody>
            <Input
              type="textarea"
              rows={4}
              placeholder={t("projectDetail.notunuzuYazin")}
              value={revisionText}
              onChange={e => setRevisionText(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setRevisionModal(false)}>{t("common:iptal")}</Button>
            <Button
              color="primary"
              onClick={handleAddRevision}
              disabled={actionLoading || !revisionText.trim()}
            >
              {actionLoading ? <Spinner size="sm" /> : t("common:ekle")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── MODAL: Durum Güncelle ─── */}
        <Modal isOpen={statusModal} toggle={() => setStatusModal(false)}>
          <ModalHeader toggle={() => setStatusModal(false)}>{t("projectDetail.projeDurumunuGuncelle")}</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("projectDetail.projeDurumu")}</Label>
              <Input type="select" value={editStatusId} onChange={e => setEditStatusId(e.target.value)}>
                <option value="">{t("projectDetail.degistirme")}</option>
                {Object.entries(PROJECT_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>{t("projectDetail.anlasmaDurumu")}</Label>
              <Input type="select" value={editDealStatus} onChange={e => setEditDealStatus(e.target.value)}>
                <option value="">{t("projectDetail.degistirme")}</option>
                {Object.entries(DEAL_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>{t("projectDetail.finansOnayi")}</Label>
              <Input type="select" value={editFinanceApproval} onChange={e => setEditFinanceApproval(e.target.value)}>
                <option value="">{t("projectDetail.degistirme")}</option>
                {Object.entries(FINANCE_APPROVAL).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>{t("projectDetail.finansNotu")}</Label>
              <Input
                type="text"
                value={editFinanceText}
                onChange={e => setEditFinanceText(e.target.value)}
                placeholder={t("projectDetail.finansAciklamasi")}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setStatusModal(false)}>{t("common:iptal")}</Button>
            <Button color="primary" onClick={handleUpdateStatus} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : t("common:kaydet")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── MODAL: Sil Onay ─── */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>{t("projectDetail.projeSil")}</ModalHeader>
          <ModalBody>
            <Alert color="warning">
              <strong>{project.standName}</strong>{" "}{t("projectDetail.projesiniSilmekIstediginizeEminMisiniz")}
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModal(false)}>{t("common:iptal")}</Button>
            <Button color="danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : t("common:sil")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

// ─── Text Content Alt Bileşeni ────────────────────────────────────────────────
function TextContentTab({ projectId, textContents, onReload }) {
  const { t: tx } = useTranslation("projeler");
  const [editing, setEditing] = useState(null); // { standFileId, text, title }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const TEXT_TYPES = [
    { standFileId: 1, label: tx("projectDetail.malzemeAciklamasi") },
    { standFileId: 3, label: tx("projectDetail.elektrikAciklamasi") },
  ];

  function getContent(standFileId) {
    return textContents.find(t => t.standFileId === standFileId);
  }

  async function handleSave() {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      await setTextContent(projectId, editing.standFileId, editing.text, editing.title);
      setEditing(null);
      await onReload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader><h6 className="mb-0">{tx("projectDetail.metinAlanlari")}</h6></CardHeader>
      <CardBody>
        {error && <Alert color="danger">{error}</Alert>}
        {TEXT_TYPES.map(tt => {
          const existing = getContent(tt.standFileId);
          const isEditing = editing?.standFileId === tt.standFileId;
          return (
            <div key={tt.standFileId} className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{tt.label}</strong>
                {!isEditing && (
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    onClick={() => setEditing({
                      standFileId: tt.standFileId,
                      text: existing?.text ?? "",
                      title: existing?.title ?? "",
                    })}
                  >
                    <Edit2 size={12} className="me-1" />
                    {existing ? tx("common:duzenle") : tx("common:ekle")}
                  </Button>
                )}
              </div>
              {isEditing ? (
                <>
                  <Input
                    type="text"
                    className="mb-2"
                    placeholder={tx("projectDetail.baslikOpsiyonel")}
                    value={editing.title}
                    onChange={e => setEditing(prev => ({ ...prev, title: e.target.value }))}
                  />
                  <Input
                    type="textarea"
                    rows={4}
                    placeholder={tx("projectDetail.icerik")}
                    value={editing.text}
                    onChange={e => setEditing(prev => ({ ...prev, text: e.target.value }))}
                  />
                  <div className="d-flex gap-2 mt-2">
                    <Button color="secondary" size="sm" onClick={() => setEditing(null)}>{tx("common:iptal")}</Button>
                    <Button color="primary" size="sm" onClick={handleSave} disabled={saving}>
                      {saving ? <Spinner size="sm" /> : tx("common:kaydet")}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-muted small">
                  {existing?.text ? (
                    <>
                      {existing.title && <div className="fw-semibold mb-1">{existing.title}</div>}
                      <div>{existing.text}</div>
                    </>
                  ) : (
                    <em>{tx("projectDetail.icerikGirilmemis")}</em>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}




